import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, Quote, Search, Sparkles } from "lucide-react";

/**
 * Text selection reader toolbar.
 * Provides authentic, verified reader actions:
 * 1. Copy text with tactile feedback
 * 2. Copy formatted blockquote with attribution
 * 3. Look up concept on Google
 * 4. Transparently prompt an AI (ChatGPT) with the exact selected passage
 */
export default function HighlightTooltip({ children }) {
  const [tip, setTip] = useState(null); // { x, y, text }
  const [copiedStatus, setCopiedStatus] = useState(null); // 'text' | 'quote' | null
  const ref = useRef(null);

  useEffect(() => {
    const onUp = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) {
        setTip(null);
        return;
      }
      const text = sel.toString().trim();
      if (!text || text.length < 3 || !ref.current) return;
      
      const range = sel.getRangeAt(0);
      if (!ref.current.contains(range.commonAncestorContainer)) return;
      
      const rect = range.getBoundingClientRect();
      const parentRect = ref.current.getBoundingClientRect();

      // Viewport-aware boundary clamping so tooltip never overflows mobile screen edges
      const rawX = rect.left - parentRect.left + rect.width / 2;
      const minX = 140;
      const maxX = Math.max(minX, parentRect.width - 140);
      const clampedX = Math.max(minX, Math.min(maxX, rawX));

      setTip({
        x: clampedX,
        y: rect.top - parentRect.top - 10,
        text,
      });
      setCopiedStatus(null);
    };

    document.addEventListener("mouseup", onUp);
    document.addEventListener("selectionchange", () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) {
        setTip(null);
        setCopiedStatus(null);
      }
    });

    return () => document.removeEventListener("mouseup", onUp);
  }, []);

  const handleCopyText = async () => {
    if (!tip?.text) return;
    try {
      await navigator.clipboard.writeText(tip.text);
      setCopiedStatus("text");
      setTimeout(() => setCopiedStatus(null), 2200);
    } catch {
      // Fallback
    }
  };

  const handleCopyQuote = async () => {
    if (!tip?.text) return;
    try {
      const quoteString = `> "${tip.text}"\n— Firza Miftahul Ilmi (fmi.dev)`;
      await navigator.clipboard.writeText(quoteString);
      setCopiedStatus("quote");
      setTimeout(() => setCopiedStatus(null), 2200);
    } catch {
      // Fallback
    }
  };

  const handleSearch = () => {
    if (!tip?.text) return;
    const query = encodeURIComponent(tip.text);
    window.open(`https://www.google.com/search?q=${query}`, "_blank", "noopener,noreferrer");
  };

  const handleExplainAI = () => {
    if (!tip?.text) return;
    const prompt = encodeURIComponent(`Please explain this concept from a software architecture article: "${tip.text}"`);
    window.open(`https://chatgpt.com/?q=${prompt}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div ref={ref} className="relative">
      {children}
      <AnimatePresence>
        {tip && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            style={{
              position: "absolute",
              left: `${tip.x}px`,
              top: `${tip.y}px`,
              transform: "translate(-50%, -100%)",
            }}
            className="z-30 flex items-center gap-1 rounded-full border border-border/80 bg-[#0d0d12]/95 backdrop-blur-md px-2 py-1 shadow-2xl"
          >
            <button
              onClick={handleCopyText}
              title="Copy selected text"
              aria-label="Copy text"
              className="h-7 px-2.5 inline-flex items-center gap-1.5 font-mono text-[11px] text-foreground/90 hover:text-foreground hover:bg-white/10 rounded-full transition-colors"
            >
              {copiedStatus === "text" ? (
                <>
                  <Check className="h-3 w-3 text-emerald-400" />
                  <span className="text-emerald-400 font-medium">Copied</span>
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3 text-muted-foreground" />
                  <span>Copy</span>
                </>
              )}
            </button>

            <span className="h-3.5 w-px bg-border/60" />

            <button
              onClick={handleCopyQuote}
              title="Copy formatted markdown quote"
              aria-label="Copy quote"
              className="h-7 px-2.5 inline-flex items-center gap-1.5 font-mono text-[11px] text-foreground/90 hover:text-foreground hover:bg-white/10 rounded-full transition-colors"
            >
              {copiedStatus === "quote" ? (
                <>
                  <Check className="h-3 w-3 text-emerald-400" />
                  <span className="text-emerald-400 font-medium">Quoted</span>
                </>
              ) : (
                <>
                  <Quote className="h-3 w-3 text-muted-foreground" />
                  <span>Quote</span>
                </>
              )}
            </button>

            <span className="h-3.5 w-px bg-border/60" />

            <button
              onClick={handleSearch}
              title="Look up concept on Google"
              aria-label="Look up concept"
              className="h-7 px-2.5 inline-flex items-center gap-1.5 font-mono text-[11px] text-foreground/90 hover:text-foreground hover:bg-white/10 rounded-full transition-colors"
            >
              <Search className="h-3 w-3 text-muted-foreground" />
              <span>Lookup</span>
            </button>

            <span className="h-3.5 w-px bg-border/60" />

            <button
              onClick={handleExplainAI}
              title="Ask ChatGPT to explain this concept"
              aria-label="Ask AI to explain"
              className="h-7 px-2.5 inline-flex items-center gap-1.5 font-mono text-[11px] text-primary hover:bg-primary/10 rounded-full transition-colors"
            >
              <Sparkles className="h-3 w-3 text-primary" />
              <span className="font-medium">Explain</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
